import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Shell } from '@/components/Shell';
import { LoginPage } from '@/pages/LoginPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { CompanyDashboardPage } from '@/pages/CompanyDashboardPage';
import { OrdersPage } from '@/pages/OrdersPage';
import { NewRechargePage } from '@/pages/NewRechargePage';
import { ManagedNumbersPage } from '@/pages/ManagedNumbersPage';
import { CompanyRequestsPage } from '@/pages/CompanyRequestsPage';
import { OrderDetailPage } from '@/pages/OrderDetailPage';
import { ExceptionsPage } from '@/pages/ExceptionsPage';
import { ReservationsPage } from '@/pages/ReservationsPage';
import { CataloguePage } from '@/pages/CataloguePage';
import { BundlesPage } from '@/pages/BundlesPage';
import { FloatPage } from '@/pages/FloatPage';
import { MerchantsPage } from '@/pages/MerchantsPage';
import { UsersPage } from '@/pages/UsersPage';
import { AuditPage } from '@/pages/AuditPage';
import { SettlementPage } from '@/pages/SettlementPage';
import { AccountingPage } from '@/pages/AccountingPage';
import { VendorPayoutsPage } from '@/pages/VendorPayoutsPage';
import { PublicCheckoutPage } from '@/pages/PublicCheckoutPage';
import { PublicCheckoutStatusPage } from '@/pages/PublicCheckoutStatusPage';
import { HomePage } from '@/pages/HomePage';
import { VendorSellPage } from '@/pages/VendorSellPage';
import { WebhooksPage } from '@/pages/WebhooksPage';
import { SettingsPage } from '@/pages/SettingsPage';

export function App() {
  return (
    <Routes>
      {/* Unauthenticated: a customer paying for their own recharge, no console session needed. */}
      <Route path="/pay" element={<PublicCheckoutPage />} />
      <Route path="/pay/status" element={<PublicCheckoutStatusPage />} />
      <Route path="*" element={<Console />} />
    </Routes>
  );
}

function Console() {
  const { session, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="grid h-screen place-items-center text-sm text-ink-500">Loading…</div>;
  }
  if (!session) {
    // "/" is the public landing page; every other console path still prompts sign-in (including
    // a bookmarked deep link to e.g. /orders — there's nothing meaningful to show without a session).
    if (location.pathname === '/') return <HomePage />;
    return <LoginPage />;
  }

  if (session.mustChangePassword) {
    return <ChangePasswordPage />;
  }

  const isVendor = session.merchantAccountType === 'VENDOR';
  const isCompany = session.merchantAccountType === 'COMPANY';

  return (
    <Shell>
      <Routes>
        <Route path="/" element={
          isVendor ? <VendorSellPage />
            // The platform admin dashboard (DashboardPage) calls platform-staff-only endpoints
            // (/api/admin/merchants, /api/admin/metrics/summary) that 403 for a company account —
            // CompanyDashboardPage is the merchant-scoped equivalent, backed by its own endpoint.
            : isCompany ? <CompanyDashboardPage />
            : <DashboardPage />
        } />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/recharges/new" element={<NewRechargePage />} />
        <Route path="/numbers" element={<ManagedNumbersPage />} />
        <Route path="/requests" element={<CompanyRequestsPage />} />
        <Route path="/orders/:id" element={<OrderDetailPage />} />
        <Route path="/exceptions" element={<ExceptionsPage />} />
        <Route path="/reservations" element={<ReservationsPage />} />
        <Route path="/catalogue" element={<CataloguePage />} />
        <Route path="/catalogue/:productId/bundles" element={<BundlesPage />} />
        <Route path="/float" element={<FloatPage />} />
        <Route path="/merchants" element={<MerchantsPage />} />
        <Route path="/settlement" element={<SettlementPage />} />
        <Route path="/accounting" element={<AccountingPage />} />
        <Route path="/vendor-payouts" element={<VendorPayoutsPage />} />
        <Route path="/webhooks" element={<WebhooksPage />} />
        <Route path="/settings/platform" element={<SettingsPage />} />
        <Route path="/settings/users" element={<UsersPage />} />
        <Route path="/settings/audit" element={<AuditPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
